/**
 * Wave C — selection & layer management commands.
 * (sel.* / lay.*; docs/architecture/command-parity-plan.md)
 */

import { runInteraction, PK, PLoopK, requireSelection, solidMesh } from './lib';
import { undoableModify, undoableAdd, undoableDelete } from '../entityOps';
import { useAppStore, type Entity } from '../../../state/store';
import { useDocumentStore } from '../../../state/useDocumentStore';
import { useLayerStateStore } from '../../../state/commandAuxStores';
// Direct import: the snapping barrel also exports the drei-based SnapIndicator.
import { useSnapStore } from '../../../tools/snapping/SnapManager';
import { createElement } from 'react';
import { Line } from '@react-three/drei';
import { SELECTION_COLORS } from '../../../themes/colors';
import type { RunnerMap } from '../runnerRegistry';
import { notify } from '../../../utils/notify';
import i18n from '../../../i18n';
import { useCommandInteract } from '../commandInteract';
import type { Pt3 } from '../geometryMath';
import {
    entitiesInCrossingPolygon, entitiesInWindowPolygon, entitiesOnFence,
} from './pathHitTest';

/** Select ledger objects whose SceneObject kind matches one of `kinds`. */
function selectByKinds(label: string, kinds: string[]): string {
    const ledger = Object.values(useDocumentStore.getState().sceneObjects);
    const ids = ledger.filter((o) => kinds.includes(o.kind)).map((o) => o.id);
    if (ids.length === 0) return `${label}: no ${kinds.join('/')} objects in the document.`;
    useAppStore.getState().select(ids);
    return `${label}: selected ${ids.length} object(s).`;
}

/** Entity type set belonging to each SceneObject kind family. */
const CAD_KIND_OF_TYPE: Record<string, string> = {
    line: 'line', polyline: 'polyline', polygon: 'polyline', rectangle: 'polyline',
    circle: 'circle', arc: 'arc', ellipse: 'ellipse', curve: 'curve',
    spline: 'curve', 'nurbs-curve': 'curve', 'nurbs-surface': 'surface', 'nurbs-polysurf': 'surface',
    text: 'text', mtext: 'text', dimension: 'dimension', tolerance: 'dimension', point: 'point',
    box: 'mesh', sphere: 'mesh', cylinder: 'mesh', solid: 'mesh',
    mesh: 'mesh', subd: 'subd', surface: 'surface', loft: 'surface', extrude: 'surface', revolve: 'surface',
    wall: 'wall', door: 'door', window: 'window', floor: 'floor', roof: 'roof',
    ceiling: 'ceiling', column: 'column', stair: 'stair', railing: 'railing', ramp: 'railing',
    component: 'generic', area: 'room', toposolid: 'mass', hatch: 'hatch', block: 'block',
    image: 'image', pointcloud: 'pointcloud',
};

function cadTypesMatching(kinds: string[]): Set<string> {
    const out = new Set<string>();
    for (const [type, kind] of Object.entries(CAD_KIND_OF_TYPE)) {
        if (kinds.includes(kind)) out.add(type);
    }
    return out;
}

// ---------------- path selection (Faz 7.8 — Fence/WP/CP) ----------------

/** The last selection set (SELECT Previous). */
let lastSelection: string[] = [];
function rememberSelection(ids: string[]): void {
    if (ids.length > 0) lastSelection = [...ids];
}

/** Shared point-loop → polygon commit. */
function startPolygonSelection(kind: 'fence' | 'wpolygon' | 'cpolygon'): string {
    const label = kind === 'fence' ? 'FENCE' : kind === 'wpolygon' ? 'WPOLYGON' : 'CPOLYGON';
    runInteraction({
        steps: [{
            prompt: kind === 'fence'
                ? 'FENCE: pick fence points (Enter to finish)'
                : `${label}: pick polygon corners (Enter to finish)`,
            kind: 'point',
            loop: true,
            minPoints: kind === 'fence' ? 2 : 3,
        }],
        // Live selection shape (Faz 11.5): the fence draws as the crossing-
        // green dashed line; the polygons close through their first corner
        // in their marquee color.
        preview: (pts, cursor) => {
            const points: [number, number, number][] = pts.map((p) => [p.x, p.y, p.z]);
            if (cursor) points.push([cursor.x, cursor.y, cursor.z]);
            if (points.length < 2) return null;
            if (kind === 'fence') {
                return createElement(Line, {
                    points,
                    color: SELECTION_COLORS.crossing,
                    lineWidth: 1.5,
                    dashed: true,
                    dashSize: 0.25,
                    gapSize: 0.15,
                });
            }
            return createElement(Line, {
                points: [...points, points[0]],
                color: kind === 'cpolygon' ? SELECTION_COLORS.crossing : SELECTION_COLORS.window,
                lineWidth: 1.5,
                dashed: kind === 'cpolygon',
                dashSize: 0.25,
                gapSize: 0.15,
            });
        },
        complete: (pts) => {
            const world = pts.map((p) => [p.x, p.y, p.z] as Pt3);
            const found = kind === 'fence'
                ? entitiesOnFence(world)
                : kind === 'wpolygon'
                    ? entitiesInWindowPolygon(world)
                    : entitiesInCrossingPolygon(world);
            if (found.length === 0) return `${label}: nothing selected.`;
            const app = useAppStore.getState();
            app.select(found);
            rememberSelection(found);
            return `${label}: ${found.length} object(s) selected.`;
        },
    });
    return `${label}: pick ${kind === 'fence' ? 'fence points' : 'polygon corners'}`;
}

/** SELECT with the classic option chips (Faz 7.8, OCS select.rs). */
function startSelect(): string {
    runInteraction({
        steps: [{
            prompt: 'SELECT: pick objects or [Window/Crossing/Fence/WPolygon/CPolygon/All/Add/Remove/Previous/Last]:',
            kind: 'pick',
            loop: true,
            minPicks: 1,
            options: [
                { keyword: 'Window' }, { keyword: 'Crossing' }, { keyword: 'Fence' },
                { keyword: 'WPolygon' }, { keyword: 'CPolygon' }, { keyword: 'All' },
                { keyword: 'Add' }, { keyword: 'Remove' }, { keyword: 'Previous' },
                { keyword: 'Last' },
            ],
        }],
        onOption: (keyword) => {
            const ci = useCommandInteract.getState();
            const app = useAppStore.getState();
            ci.cancelActive();
            switch (keyword) {
                case 'FENCE':
                    startPolygonSelection('fence');
                    return;
                case 'WPOLYGON':
                    startPolygonSelection('wpolygon');
                    return;
                case 'CPOLYGON':
                    startPolygonSelection('cpolygon');
                    return;
                case 'ALL':
                    app.select(Object.keys(app.entities));
                    rememberSelection(Object.keys(app.entities));
                    return;
                case 'LAST': {
                    const ids = Object.keys(app.entities);
                    if (ids.length > 0) {
                        app.select([ids[ids.length - 1]]);
                        rememberSelection([ids[ids.length - 1]]);
                    }
                    return;
                }
                case 'PREVIOUS':
                    if (lastSelection.length > 0) app.select(lastSelection);
                    return;
                case 'ADD': {
                    // Add the pick-step accumulation to the live selection.
                    if (ci.picks.length > 0) {
                        app.addToSelection([...new Set([...app.selectedIds, ...ci.picks])]);
                        rememberSelection(app.selectedIds);
                    }
                    startSelect();
                    return;
                }
                case 'REMOVE': {
                    if (ci.picks.length > 0) {
                        const drop = new Set(ci.picks);
                        const next = app.selectedIds.filter((id) => !drop.has(id));
                        app.select(next);
                        rememberSelection(next);
                    }
                    startSelect();
                    return;
                }
                default:
                    // Window/Crossing route into the polygon flows with the
                    // equivalent semantics (window = WP of a rectangle).
                    startPolygonSelection(keyword === 'CROSSING' ? 'cpolygon' : 'wpolygon');
                    return;
            }
        },
        complete: (_p, _v, picks) => {
            if (picks.length === 0) return 'SELECT: nothing picked.';
            const app = useAppStore.getState();
            app.addToSelection([...new Set([...app.selectedIds, ...picks])]);
            rememberSelection(app.selectedIds);
            return `SELECT: ${picks.length} object(s) added to the selection.`;
        },
    });
    return 'SELECT: pick objects or use the option chips';
}

/** Insert/block reference = an entity carrying a blockName. */
function isBlockEntity(e: Entity): boolean {
    return !!e.data.blockName;
}

export const selectRunners: RunnerMap = {
    'sel.select': () => startSelect(),
    'sel.fence': () => startPolygonSelection('fence'),
    'sel.wpolygon': () => startPolygonSelection('wpolygon'),
    'sel.cpolygon': () => startPolygonSelection('cpolygon'),

    'sel.surfaces': () => selectByKinds('SELSRF', ['surface']),
    'sel.polysrf': () => selectByKinds('SELPOLYSRF', ['surface']),
    'sel.meshes': () => selectByKinds('SELMESH', ['mesh']),
    'sel.subd': () => selectByKinds('SELSUBD', ['subd']),

    'sel.duplicates': () => {
        const { entities, select } = useAppStore.getState();
        const seen = new Map<string, string>();
        const dupes: string[] = [];
        Object.values(entities).forEach((e) => {
            const key = JSON.stringify([e.type, e.data.start ?? e.data.center ?? e.data.position ?? null,
                e.data.end ?? null, e.data.vertices ? Math.round(polyKey(e)) : null]);
            const hit = seen.get(key);
            if (hit) dupes.push(e.id, hit);
            else seen.set(key, e.id);
        });
        if (dupes.length === 0) return 'SELDUP: no duplicates found.';
        select([...new Set(dupes)]);
        return `SELDUP: ${new Set(dupes).size} overlapping object(s) selected.`;
    },

    'sel.last': () => {
        const { entities, select } = useAppStore.getState();
        const ids = Object.keys(entities);
        if (ids.length === 0) return 'SELLAST: document is empty.';
        // Entities are insert-ordered in the map; last key = newest.
        select([ids[ids.length - 1]]);
        return `SELLAST: selected "${entities[ids[ids.length - 1]].name}".`;
    },

    'sel.qselect': (args) => {
        // QSELECT type [layer] — filters the whole document.
        const type = args?.[0]?.toLowerCase();
        const layer = args?.[1];
        if (!type) {
            notify(i18n.t('tools.notify.qselectUsageQselectTypeLayerE'), 'info');
            return 'QSELECT: specify object type.';
        }
        const { entities, select } = useAppStore.getState();
        const kind = CAD_KIND_OF_TYPE[type];
        const ids = Object.values(entities)
            .filter((e) => (kind ? cadTypesMatching([kind]).has(e.type) : e.type === type)
                && (!layer || e.layer === layer))
            .map((e) => e.id);
        if (ids.length === 0) return `QSELECT: no matching objects (${type}${layer ? ' on ' + layer : ''}).`;
        select(ids);
        return `QSELECT: ${ids.length} object(s) selected.`;
    },

    'sel.filter': (args) => selectRunners['sel.qselect'](args),

    'sel.ungroup': () => {
        const list = requireSelection('UNGROUP');
        if (!list) return 'UNGROUP: nothing selected.';
        const grouped = list.filter((e) => e.data.groupId);
        if (grouped.length === 0) return 'UNGROUP: selected objects are not grouped.';
        undoableModify(grouped.map((e) => e.id), (d) => { delete d.groupId; return d; });
        return `UNGROUP: ${grouped.length} object(s) ungrouped.`;
    },

    'sel.showSelected': () => {
        runInteraction({
            steps: [PLoopK('SHOWSELECTED: click hidden objects to reveal', 1)],
            complete: (_p, _v, picks) => {
                picks.forEach((id) => useDocumentStore.getState().patchSceneObject(id, { visible: true }));
                const { entities, updateEntity } = useAppStore.getState();
                picks.forEach((id) => { if (entities[id]) updateEntity(id, { visible: true }); });
                return `SHOWSELECTED: ${picks.length} object(s) revealed.`;
            },
        });
        return 'SHOWSELECTED: click hidden objects';
    },

    'sel.unlockSelected': () => {
        const list = requireSelection('UNLOCKSELECTED');
        if (!list) return 'UNLOCKSELECTED: nothing selected.';
        const { patchSceneObject } = useDocumentStore.getState();
        list.forEach((e) => patchSceneObject(e.id, { locked: false }));
        return `UNLOCKSELECTED: ${list.length} object(s) unlocked.`;
    },

    'sel.cycle': (args) => {
        // SELECTIONCYCLING sysvar parity (Faz 4.4): 0/1 toggles the stacked
        // pick display mode — with it on, a click that pierces several
        // entities opens the candidate popup (SelCyclePopup) instead of
        // silently advancing. The old rotate-selection demo moves under
        // the popup's own click flow.
        const snap = useSnapStore.getState();
        const raw = args?.[0]?.trim().toUpperCase();
        if (raw === '1' || raw === 'ON') {
            if (!snap.selectionCycling) snap.toggleSelectionCycling();
            return 'SELECTIONCYCLING: stacked-pick popup ON.';
        }
        if (raw === '0' || raw === 'OFF') {
            if (snap.selectionCycling) snap.toggleSelectionCycling();
            return 'SELECTIONCYCLING: stacked-pick popup OFF.';
        }
        snap.toggleSelectionCycling();
        return `SELECTIONCYCLING: stacked-pick popup ${!snap.selectionCycling ? 'ON' : 'OFF'}.`;
    },

    // ------------------------------ layers ------------------------------
    'lay.off': () => {
        runInteraction({
            steps: [PK('LAYOFF: pick an object on the layer to turn off')],
            complete: (_p, _v, picks) => toggleLayerFor(picks[0], false, 'LAYOFF'),
        });
        return 'LAYOFF: pick an object';
    },
    'lay.on': () => {
        const app = useAppStore.getState();
        let turned = 0;
        Object.values(app.layers).forEach((l) => {
            if (!l.visible) { app.updateLayer(l.id, { visible: true }); turned++; }
        });
        return turned > 0 ? `LAYON: ${turned} layer(s) turned on.` : 'LAYON: all layers already on.';
    },
    'lay.freeze': () => {
        runInteraction({
            steps: [PK('LAYFRZ: pick an object on the layer to freeze')],
            complete: (_p, _v, picks) => {
                const layerId = layerIdFor(picks[0]);
                if (!layerId) return 'LAYFRZ: could not resolve the layer.';
                const app = useAppStore.getState();
                app.updateLayer(layerId, { visible: false, frozen: true } as never);
                return `LAYFRZ: layer "${layerName(layerId)}" frozen.`;
            },
        });
        return 'LAYFRZ: pick an object';
    },
    'lay.thaw': () => {
        const app = useAppStore.getState();
        let thawed = 0;
        Object.values(app.layers).forEach((l) => {
            if ((l as unknown as Record<string, unknown>).frozen) {
                app.updateLayer(l.id, { visible: true, frozen: false } as never);
                thawed++;
            }
        });
        return thawed > 0 ? `LAYTHW: ${thawed} layer(s) thawed.` : 'LAYTHW: no frozen layers.';
    },
    'lay.lock': () => {
        runInteraction({
            steps: [PK('LAYLOCK: pick an object on the layer to lock')],
            complete: (_p, _v, picks) => {
                const layerId = layerIdFor(picks[0]);
                if (!layerId) return 'LAYLOCK: could not resolve the layer.';
                useAppStore.getState().updateLayer(layerId, { locked: true });
                return `LAYLOCK: layer "${layerName(layerId)}" locked.`;
            },
        });
        return 'LAYLOCK: pick an object';
    },
    'lay.unlock': () => {
        const app = useAppStore.getState();
        let unlocked = 0;
        Object.values(app.layers).forEach((l) => {
            if (l.locked) { app.updateLayer(l.id, { locked: false }); unlocked++; }
        });
        return unlocked > 0 ? `LAYULK: ${unlocked} layer(s) unlocked.` : 'LAYULK: no locked layers.';
    },
    'lay.merge': () => {
        runInteraction({
            steps: [PK('LAYMRG: pick an object on the SOURCE layer'), PK('LAYMRG: pick an object on the TARGET layer')],
            complete: (_p, _v, picks) => {
                const from = layerIdFor(picks[0]), to = layerIdFor(picks[1]);
                if (!from || !to || from === to) return 'LAYMRG: pick objects on two different layers.';
                mergeLayers(from, to);
                return `LAYMRG: "${layerName(from)}" merged into "${layerName(to)}".`;
            },
        });
        return 'LAYMRG: pick source then target objects';
    },
    'lay.delete': () => {
        runInteraction({
            steps: [PK('LAYDEL: pick an object on the layer to delete')],
            complete: (_p, _v, picks) => {
                const layerId = layerIdFor(picks[0]);
                if (!layerId) return 'LAYDEL: could not resolve the layer.';
                if (layerId === 'layer-0') return 'LAYDEL: the default layer cannot be deleted.';
                // Delete the layer's entities first, then the layer itself.
                const { entities, select } = useAppStore.getState();
                const layer = Object.values(useAppStore.getState().layers).find((l) => l.id === layerId);
                const doomed = Object.values(entities).filter((e) => e.layer === (layer?.name ?? layerId)).map((e) => e.id);
                const ledger = Object.values(useDocumentStore.getState().sceneObjects)
                    .filter((o) => o.layerId === (layer?.name ?? layerId)).map((o) => o.id);
                undoableDelete([...new Set([...doomed, ...ledger])]);
                select([]);
                useAppStore.getState().deleteLayer(layerId);
                return `LAYDEL: layer "${layer?.name}" and ${doomed.length + ledger.length} object(s) deleted.`;
            },
        });
        return 'LAYDEL: pick an object';
    },
    'lay.rename': (args) => {
        const target = args?.join(' ')?.trim();
        if (!target) {
            runInteraction({
                steps: [PK('RENAME: pick a layer object'), { prompt: 'RENAME: new layer name', kind: 'text' }],
                complete: (_p, values, picks) => renameLayerFor(picks[0], String(values[1] ?? '')),
            });
            return 'RENAME: pick an object';
        }
        const list = requireSelection('RENAME');
        if (!list || list.length === 0) return 'RENAME: pick/select an object first.';
        // "RENAME NewName" renames the active object's layer.
        const layerId = layerIdFor(list[0].id);
        return renameLayerFor(list[0].id, target, layerId ?? undefined);
    },

    // LAYERSTATE: save/restore the whole layer display+style snapshot.
    // States live in a persisted named map (LayerSTATE Manager parity); the
    // Faz 5 manager dialog saves the rich record + restore mask, this runner
    // keeps the same shape (full mask when saved from the command line).
    'lay.state': (args) => {
        const action = (args?.[0] ?? 'list').toLowerCase();
        const app = useAppStore.getState();
        if (action === 'save') {
            const name = args?.[1]?.trim() || `State-${new Date().toLocaleTimeString()}`;
            useLayerStateStore.getState().save(name, Object.values(app.layers).map((l) => ({
                id: l.id, visible: l.visible, frozen: !!l.frozen, locked: l.locked,
                current: app.activeLayerId === l.id,
                color: l.color, linetype: l.linetype, lineweight: l.lineweight,
                transparency: l.transparency, plottable: l.plottable !== false,
            })), ['ON', 'FROZEN', 'LOCKED', 'COLOR', 'LINE_TYPE', 'LINE_WEIGHT', 'TRANSPARENCY']);
            return `LAYERSTATE: saved "${name}".`;
        }
        if (action === 'restore') {
            const store = useLayerStateStore.getState();
            const name = args?.[1]?.trim();
            const hit = name ? store.states[name] : Object.values(store.states).slice(-1)[0];
            const hitName = name ?? (Object.keys(store.states).length ? Object.keys(store.states).slice(-1)[0] : '');
            if (!hit || !hitName) return 'LAYERSTATE: no named state to restore (save one first).';
            const mask = new Set(store.masks[hitName] ?? ['ON', 'FROZEN', 'LOCKED', 'COLOR', 'LINE_TYPE', 'LINE_WEIGHT', 'TRANSPARENCY']);
            hit.forEach((rec) => {
                const layer = Object.values(app.layers).find((l) => l.id === rec.id)
                    ?? Object.values(app.layers).find((l) => l.name === rec.id);
                if (!layer) return;
                const updates: Record<string, unknown> = {};
                if (mask.has('ON')) updates.visible = rec.visible;
                if (mask.has('FROZEN')) updates.frozen = rec.frozen;
                if (mask.has('LOCKED')) updates.locked = rec.locked;
                if (mask.has('COLOR') && rec.color) updates.color = rec.color;
                if (mask.has('LINE_TYPE') && rec.linetype !== undefined) updates.linetype = rec.linetype;
                if (mask.has('LINE_WEIGHT') && rec.lineweight !== undefined) updates.lineweight = rec.lineweight;
                if (mask.has('TRANSPARENCY') && rec.transparency !== undefined) updates.transparency = rec.transparency;
                app.updateLayer(layer.id, updates);
                if (rec.current) app.setActiveLayer(layer.id);
            });
            return `LAYERSTATE: restored "${hitName}".`;
        }
        if (action === 'delete' && args?.[1]) {
            useLayerStateStore.getState().remove(args[1].trim());
            return `LAYERSTATE: deleted "${args[1].trim()}".`;
        }
        const names = Object.keys(useLayerStateStore.getState().states);
        return names.length ? `LAYERSTATE: ${names.join(', ')}` : 'LAYERSTATE: no saved states.';
    },
};

// ---------------- helpers ----------------
function polyKey(e: Entity): number {
    const v = (e.data.vertices as number[][]) ?? [];
    return v.reduce((acc, p) => acc + p[0] * 31.7 + p[1] * 17.3, 0);
}

function layerName(id: string): string {
    return useAppStore.getState().layers[id]?.name ?? id;
}

/** Resolve the layer id for an entity (layer name stored on the entity). */
function layerIdFor(entityId: string | undefined): string | null {
    if (!entityId) return null;
    const { entities, layers } = useAppStore.getState();
    const e = entities[entityId];
    const name = e?.layer;
    if (name) {
        const hit = Object.values(layers).find((l) => l.name === name || l.id === name);
        if (hit) return hit.id;
    }
    const scene = useDocumentStore.getState().sceneObjects[entityId];
    if (scene?.layerId) {
        const hit = Object.values(layers).find((l) => l.name === scene.layerId || l.id === scene.layerId);
        if (hit) return hit.id;
    }
    return null;
}

function toggleLayerFor(entityId: string | undefined, visible: boolean, label: string): string {
    const layerId = layerIdFor(entityId);
    if (!layerId) return `${label}: could not resolve the layer.`;
    useAppStore.getState().updateLayer(layerId, { visible });
    return `${label}: layer "${layerName(layerId)}" ${visible ? 'on' : 'off'}.`;
}

function mergeLayers(fromId: string, toId: string): void {
    const app = useAppStore.getState();
    const from = app.layers[fromId], to = app.layers[toId];
    if (!from || !to) return;
    // Move entities (CAD store) — ledger objects re-mirror through updateEntity.
    Object.values(app.entities).forEach((e) => {
        if (e.layer === from.name) app.updateEntity(e.id, { layer: to.name });
    });
    Object.values(app.entities).forEach((e) => {
        if (e.layer === from.id) app.updateEntity(e.id, { layer: to.id });
    });
    app.deleteLayer(fromId);
}

function renameLayerFor(entityId: string | undefined, newName: string, knownId?: string): string {
    const layerId = knownId ?? layerIdFor(entityId);
    if (!layerId) return 'RENAME: could not resolve the layer.';
    if (!newName) return 'RENAME: no new name given.';
    const app = useAppStore.getState();
    const old = app.layers[layerId];
    if (!old) return 'RENAME: layer not found.';
    if (layerId === 'layer-0') return 'RENAME: the default layer cannot be renamed.';
    app.updateLayer(layerId, { name: newName });
    // Re-tag entities carrying the old name.
    Object.values(app.entities).forEach((e) => {
        if (e.layer === old.name) app.updateEntity(e.id, { layer: newName });
    });
    return `RENAME: layer "${old.name}" renamed to "${newName}".`;
}
